import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { t } from 'utilities/TranslationUtils';
import { MeasurementDetailDto } from 'types/models/MeasurementDetail';

type VideoSource = MeasurementDetailDto['videos'][number];

interface VideoModalProps {
  videos: VideoSource[];
  initialIndex: number;
  onClose: () => void;
}

const labelOf = (v: VideoSource): string => v.label ?? v.videoId;

/**
 * Lightweight video lightbox. All angles stay mounted (only the selected one visible), so switching angle is
 * instant: the other clips already have their metadata loaded, and the playback position and play/pause state
 * carry over so the angles stay in sync. ←/→ switch angle, Esc closes.
 */
const VideoModal: React.FunctionComponent<VideoModalProps> = ({ videos, initialIndex, onClose }) => {
  const [index, setIndex] = useState(Math.min(initialIndex, Math.max(0, videos.length - 1)));
  const refs = useRef<(HTMLVideoElement | null)[]>([]);
  const pendingSync = useRef<{ time: number; play: boolean } | null>(null);

  const select = useCallback((next: number) => {
    if (next === index || next < 0 || next >= videos.length) return;
    const current = refs.current[index];
    pendingSync.current = { time: current?.currentTime ?? 0, play: current ? !current.paused : true };
    current?.pause();
    setIndex(next);
  }, [index, videos.length]);

  // Start playback of the newly visible angle at the previous angle's position.
  useEffect(() => {
    const video = refs.current[index];
    if (!video) return;
    const sync = pendingSync.current;
    pendingSync.current = null;
    if (sync) video.currentTime = sync.time;
    if (!sync || sync.play) void video.play().catch(() => undefined);
  }, [index]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') select(index + 1);
      else if (e.key === 'ArrowLeft') select(index - 1);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [index, onClose, select]);

  if (videos.length === 0) return null;

  return createPortal(
    <div
      className='video-modal'
      role='dialog'
      aria-modal='true'
      aria-label={labelOf(videos[index])}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-test='video-modal'
    >
      <div className='video-modal__panel'>
        <div className='video-modal__header'>
          <span className='video-modal__title'>{labelOf(videos[index])}</span>
          <button type='button' className='video-modal__close' onClick={onClose} aria-label={t('measurementDetail.videoClose')} data-test='video-modal-close'>
            ×
          </button>
        </div>

        <div className='video-modal__stage'>
          {videos.map((v, i) => (
            <video
              key={v.videoId}
              ref={(el) => { refs.current[i] = el; }}
              className='video-modal__video'
              style={{ display: i === index ? 'block' : 'none' }}
              src={v.url}
              poster={v.thumbnailUrl ?? undefined}
              preload={i === index ? 'auto' : 'metadata'}
              controls
              playsInline
            >
              {t('measurementDetail.videoUnsupported')}
            </video>
          ))}
        </div>

        {videos.length > 1 && (
          <div className='video-modal__angles' role='tablist' data-test='video-modal-angles'>
            {videos.map((v, i) => (
              <button
                key={v.videoId}
                type='button'
                role='tab'
                aria-selected={i === index}
                className={`video-modal__angle${i === index ? ' is-active' : ''}`}
                onClick={() => select(i)}
              >
                {v.thumbnailUrl ? <img src={v.thumbnailUrl} alt='' loading='lazy' /> : <span className='video-modal__angle-placeholder' />}
                <span>{labelOf(v)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default VideoModal;
